/**
 * Single place that knows how the web reads the product-variant catalog.
 *
 * ONE collection behind the alias `ListingProductVariant`. Only `displayName`,
 * `categories[].name`, `attributes[].name` and `attributes[].value` are stored
 * per language, as `<field>_<lang>`.
 *
 * Every request/response boundary goes through this file so the rest of the
 * feature keeps working with the single-language document shape it always had.
 * Keep it free of React so it stays unit-testable.
 */

export const CATALOG_LANGS = ["en", "tr", "de", "es", "fr", "ar"] as const;
export type CatalogLang = (typeof CATALOG_LANGS)[number];

/** UI store language name -> language code used by the merged collection. */
export const LANGUAGE_TO_CATALOG_LANG: Record<string, CatalogLang> = {
  Turkish: "tr",
  English: "en",
  German: "de",
  Arabic: "ar",
  Spanish: "es",
  French: "fr",
  // The catalog has no Italian fields yet: Italian reads the English ones.
  Italian: "en",
};

/** Alias of the merged product-variant collection (shared by all languages). */
export const PRODUCT_COLLECTION = "ListingProductVariant";

/** Language code for a UI store language name. Falls back to Turkish (today's default). */
export function getCatalogLang(language?: string): CatalogLang {
  return (language && LANGUAGE_TO_CATALOG_LANG[language]) || "tr";
}

/** The four fields that exist once per language in a merged document. */
const LOCALIZED_BASES = [
  "displayName",
  "categories.name",
  "attributes.name",
  "attributes.value",
] as const;

function isLocalizedBase(name: string): boolean {
  return (LOCALIZED_BASES as readonly string[]).includes(name);
}

/** `categories.name` -> `categories.name_tr`; any other name is returned as is. */
export function localizedFieldName(name: string, lang: CatalogLang): string {
  return isLocalizedBase(name) ? `${name}_${lang}` : name;
}

export type QueryByVariant = "main" | "suggestions" | "preferenceBrands";

/**
 * A localized field comes FIRST in every list: Typesense normalizes the query
 * text with the locale of the leading field, and `name` has none, so with it
 * in front "gömlek" became "gomlek" and missed every `_tr` field.
 */
const QUERY_BY_FIELDS: Record<QueryByVariant, readonly string[]> = {
  main: [
    "displayName",
    "name",
    "brand.name",
    "categories.name",
    "attributes.name",
    "attributes.value",
  ],
  suggestions: ["displayName", "name", "brand.name", "categories.name"],
  preferenceBrands: [
    "displayName",
    "name",
    "brand.name",
    "categories.name",
    "attributes.name",
    "attributes.value",
  ],
};

/** `query_by` value for a request, localized to `lang`. */
export function queryByFields(
  lang: CatalogLang,
  variant: QueryByVariant = "main",
): string {
  return QUERY_BY_FIELDS[variant]
    .map((field) => localizedFieldName(field, lang))
    .join(",");
}

/**
 * `include_fields` value for a request. The localized fields are suffixed and
 * the nested `attributes` / `categories` objects are trimmed to the sub-fields
 * the app reads, in one language only.
 */
export function includeFields(
  lang: CatalogLang,
  list: string | readonly string[],
): string {
  const tokens = (typeof list === "string" ? list.split(",") : [...list])
    .map((token) => token.trim())
    .filter(Boolean);

  const out: string[] = [];
  for (const token of tokens) {
    if (token === "attributes") {
      out.push(
        "attributes.attrId",
        "attributes.attributeValueId",
        "attributes.isVariantable",
        `attributes.name_${lang}`,
        `attributes.value_${lang}`,
      );
    } else if (token === "categories") {
      out.push(
        "categories.categoryId",
        `categories.name_${lang}`,
        "categories.url",
      );
    } else {
      out.push(localizedFieldName(token, lang));
    }
  }
  return out.join(",");
}

type AnyRecord = Record<string, unknown>;

function isRecord(value: unknown): value is AnyRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isBlank(value: unknown): boolean {
  return value === undefined || value === null || value === "";
}

/**
 * Collapse the `<base>_<lang>` keys of one flat object into `<base>`:
 * lang -> en -> first non-empty language. An object without any suffixed key
 * for a base (legacy single-language shape) keeps that base untouched.
 */
function collapseSuffixed(
  obj: AnyRecord,
  bases: readonly string[],
  lang: CatalogLang,
): AnyRecord {
  const result: AnyRecord = { ...obj };
  for (const base of bases) {
    const keys = CATALOG_LANGS.map((l) => `${base}_${l}`);
    if (!keys.some((key) => key in obj)) continue;

    let chosen = [`${base}_${lang}`, `${base}_en`, ...keys]
      .map((key) => obj[key])
      .find((value) => !isBlank(value));
    // Nothing usable in any language: keep what the requested one held (null
    // or ''), rather than inventing a value or dropping a key that callers
    // test for.
    if (chosen === undefined) chosen = obj[`${base}_${lang}`];

    for (const key of keys) delete result[key];
    if (chosen !== undefined) result[base] = chosen;
  }
  return result;
}

function collapseEntries(
  entries: unknown,
  bases: readonly string[],
  lang: CatalogLang,
): unknown {
  if (!Array.isArray(entries)) return entries;
  return entries.map((entry) =>
    isRecord(entry) ? collapseSuffixed(entry, bases, lang) : entry,
  );
}

/**
 * Map a merged document back to the single-language shape the app reads:
 * `displayName`, `categories[].name`, `attributes[].name` and `.value` are set
 * for `lang` and every suffixed key is dropped. Pure: the input is never
 * mutated.
 */
export function localizeHit<T>(doc: T, lang: CatalogLang): T {
  if (!isRecord(doc)) return doc;

  const source: AnyRecord = doc;
  const result = collapseSuffixed(source, ["displayName"], lang);
  if ("categories" in source) {
    result.categories = collapseEntries(source.categories, ["name"], lang);
  }
  if ("attributes" in source) {
    result.attributes = collapseEntries(
      source.attributes,
      ["name", "value"],
      lang,
    );
  }
  return result as T;
}

/**
 * Same mapping for the `parent` object Typesense returns with a nested facet
 * value (`facet_return_parent`): one `attributes`, `categories` or `brand`
 * entry. A brand parent has no suffixed keys and passes through untouched.
 */
export function localizeFacetParent<T>(parent: T, lang: CatalogLang): T {
  if (Array.isArray(parent)) {
    return parent.map((item) => localizeFacetParent(item, lang)) as unknown as T;
  }
  if (!isRecord(parent)) return parent;
  return collapseSuffixed(parent, ["name", "value"], lang) as T;
}
